import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useLogging } from "@/shared/hooks/useLogging";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { MessageCircle, User, Clock, Search, Bot, Loader2, Eye, X, Handshake, ArrowLeft } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Fragment } from 'react';
import { AppLayout } from '@/components/layouts/AppLayout';
import { getTenantCustomersWithConversations, getCustomerIdForConversation } from '@/lib/tenant-conversations-client';
import { RealtimeChat } from '@/components/chat/RealtimeChat';
import { supabase } from '@/integrations/supabase/client';
import { AIMessage } from '@/modules/ai-management/types/ai-types';

interface MessageUpdate {
  type: 'sent' | 'received' | 'status_update';
  message?: AIMessage;
  conversationId: string;
  customerId: string;
  customerPhone?: string;
  customerName?: string;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  whatsappMessageId?: string;
  lastMessageContent?: string;
  lastMessageAt?: string;
  unreadCount?: number;
}
import { useWebhookErrorDetection } from '@/hooks/useWebhookErrorDetection';
import { WebhookErrorAlert } from '@/components/whatsapp/WebhookErrorAlert';
import { useAIAgentSettings } from '@/hooks/useAIAgentSettings';
import { AIModeControl } from '@/components/chat/AIModeControl';
import { ConversationAIModeIndicator } from '@/components/chat/ConversationAIModeIndicator';
import { useConversationAIModes } from '@/hooks/useConversationAIModes';
import { BulkAIModeControls } from '@/components/conversations/BulkAIModeControls';
import { MessageStatsCounter } from '@/components/conversations/MessageStatsCounter';
import { AssignmentDropdown } from '@/components/conversations/AssignmentDropdown';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { UserImpersonationDropdown } from '@/components/platform-admin/UserImpersonationDropdown';
import { ImpersonationBanner } from '@/components/platform-admin/ImpersonationBanner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { WhatsAppTemplateSelector } from '@/components/chat/WhatsAppTemplateSelector';
import { EscalationBadge, EscalationTrigger } from '@/components/chat/EscalationBadge';
import { EscalationContextPanel } from '@/components/chat/EscalationContextPanel';
import { ResetConversationDialog } from '@/modules/conversations/components/ResetConversationDialog';
import { ConversationsVersionToggle } from '@/modules/conversations/components/ConversationsVersionToggle';
import { formatDistanceToNow } from 'date-fns';
import { es as dateFnsEs, enUS as dateFnsEnUS } from 'date-fns/locale';

interface ConversationsProps {
  platformAdminTenantId?: string;
}

interface Customer {
  id: string;
  name: string;
  phone_number: string;
  external_id: string;
  last_interaction: string;
}

interface ConversationWithDetails {
  id: string;
  customer_id: string;
  status: 'open' | 'expiring_soon' | 'template_only' | 'closed';
  priority: number;
  last_message_at: string;
  created_at: string;
  customer?: Customer;
  customer_name?: string;
  customer_phone?: string;
  customer_username?: string;
  customer_whatsapp_id?: string;
  unread_count?: number;
  last_message?: {
    content: string;
    sender_type: string;
    created_at: string;
  };
  // WhatsApp 24h window timing information
  window_expires_at?: string;
  window_time_remaining_hours?: number;
  is_window_active?: boolean;
  // AI mode for the conversation
  agent_mode?: 'auto' | 'suggestions' | 'manual';
  // Message statistics
  total_message_count?: number;
  customer_message_count?: number;
  ai_auto_message_count?: number;
  ai_assist_message_count?: number;
  manual_message_count?: number;
  // Escalation tracking
  escalation_status?: 'none' | 'pending' | 'resolved';
  // Inbox categorization (Phase 1A)
  inbox_category?: 'agent_inbox' | 'resolved' | 'online' | 'closed';
  platform_metadata?: {
    escalation_context?: {
      trigger: EscalationTrigger;
      reason: string;
      customer_message?: string;
      recent_messages?: Array<{
        role: 'user' | 'assistant';
        content: string;
        timestamp: string;
      }>;
      error_details?: {
        error_message?: string;
        frustration_score?: number;
        indicators?: string[];
      };
      escalated_at?: string; // Backend uses 'escalated_at', not 'timestamp'
    };
  };
  // Assigned user (Last Touch Wins)
  assigned_user_id?: string;
  assigned_user_name?: string;
  assigned_user_email?: string;
  // Partner info (from partner_directory)
  partner_name?: string;
}

// Customer-centric model: One row per customer with latest conversation data
interface CustomerWithConversations {
  customer_id: string;
  customer_name: string;
  customer_phone: string;
  customer_whatsapp_id: string;
  conversation_count: number;
  all_conversation_ids: string[];
  has_pending_escalation: boolean;
  total_unread_count: number;
  // Latest conversation fields
  latest_conversation_id: string;
  status: string;
  priority: number;
  last_message_at: string;
  created_at: string;
  agent_mode: string;
  inbox_category: string;
  escalation_status: string;
  platform_metadata: unknown;
  assigned_user_id: string;
  assigned_user_name: string;
  assigned_user_email: string;
  thread_id: string;
  thread_expires_at: string;
  is_test_conversation: boolean;
  last_message_content: string;
  last_message_sender_type: string;
  total_message_count: number;
  customer_message_count: number;
  ai_auto_message_count: number;
  ai_assist_message_count: number;
  manual_message_count: number;
  window_expires_at: string;
  window_time_remaining_hours: number;
  is_window_active: boolean;
  partner_name: string;
}

/**
 * Fixed action bar for the AI/context panel — always reachable regardless of
 * which content the panel above it is showing (escalation details or the
 * "AI Panel" placeholder). Hosts ResetConversationDialog so an operator can
 * unstick a conversation without hunting for it. Renders nothing until a
 * conversation and its tenant schema are known.
 */
function StuckConversationActions({
  tenantSchema,
  conversationId,
}: {
  tenantSchema?: string;
  conversationId?: string | null;
}) {
  const { t } = useTranslation('conversations');
  if (!tenantSchema || !conversationId) return null;
  return (
    <div className="border-t p-4 shrink-0 bg-background">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        {t('conversationActions')}
      </h4>
      <ResetConversationDialog tenantSchema={tenantSchema} conversationId={conversationId} />
    </div>
  );
}

const Conversations: React.FC<ConversationsProps> = ({ platformAdminTenantId }) => {
  const { t, i18n } = useTranslation(['conversations', 'common', 'notifications']);
  const { user, session, tenantId, userProfile } = useAuth();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { info, error, debug } = useLogging('conversations');
  const isMobile = useIsMobile();
  const deepLinkIdRef = useRef(searchParams.get('id'));

  // Refs for callback values (used inside setupRealtimeSubscription)
  const userProfileRef = useRef(userProfile);
  const toastRef = useRef(toast);
  const tRef = useRef(t);
  // Cache user ID to prevent filter failures during re-renders
  const currentUserIdRef = useRef<string | null>(null);

  // Use platform admin tenant ID if provided, otherwise use regular tenant ID
  const effectiveTenantId = platformAdminTenantId || tenantId;

  /**
   * The app frame, EXCEPT when this screen is already inside one.
   *
   * On `/platform-admin/tenant/:tenantId/conversations` this screen mounts inside
   * `TenantManagementLayout`, which already draws the platform-admin sidebar and the tenant
   * header. Wrapping again in `AppLayout` drew a SECOND sidebar: a sidebar-wide empty band on the
   * left and the inbox pushed off-centre. Settled on 2026-09-06 by rendering this screen and the
   * new one side by side on that route — both had the identical band, so it was never a
   * regression of the new screen, and both are fixed here. `Fragment` rather than a `<div>`: the
   * frame is being removed, not replaced, and an extra element would change the flex chain.
   */
  const Frame = platformAdminTenantId ? Fragment : AppLayout;
  // Customer-centric state (replaces per-conversation state)
  const [customerGroups, setCustomerGroups] = useState<CustomerWithConversations[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithConversations | null>(null);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [aiModeFilter, setAiModeFilter] = useState<'all' | 'auto' | 'suggestions' | 'manual'>('all');
  const [inboxView, setInboxView] = useState<'active' | 'resolved' | 'all'>('active');
  const [assignedAgentFilter, setAssignedAgentFilter] = useState<string>('all');
  const [currentTenant, setCurrentTenant] = useState<Record<string, unknown> | null>(null);
  const [conversationsNeedingAttention, setConversationsNeedingAttention] = useState<Set<string>>(new Set());
  const [tenantLoading, setTenantLoading] = useState(true);

  // Pagination state for infinite scroll
  const [conversationPage, setConversationPage] = useState(0);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalConversationCount, setTotalConversationCount] = useState(0);

  const isInitialLoad = useRef(true);
  const [showTemplateSelectorDialog, setShowTemplateSelectorDialog] = useState(false);
  const [templateSelectorConversation, setTemplateSelectorConversation] = useState<CustomerWithConversations | null>(null);
  // Search debounce ref for server-side search
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const fetchCustomerGroupsRef = useRef<() => void>(() => {});

  // V1411: Message draft persistence across conversation switches
  const [messageDrafts, setMessageDrafts] = useState<Map<string, string>>(() => {
    // Optional: Restore from localStorage on page load
    try {
      const stored = localStorage.getItem('okos_conversation_drafts');
      console.log('[V1411] 🔍 Restoring drafts from localStorage:', stored);
      if (stored) {
        const entries = JSON.parse(stored);
        const restoredMap = new Map(entries);
        console.log('[V1411] ✅ Restored', restoredMap.size, 'draft(s):', Array.from(restoredMap.entries()));
        return restoredMap;
      }
    } catch (error) {
      console.error('[V1411] ❌ Failed to restore drafts from localStorage:', error);
    }
    console.log('[V1411] 📝 Starting with empty drafts Map');
    return new Map();
  });

  // Mobile-specific state: Toggle between chat and context panel
  const [showMobileContextPanel, setShowMobileContextPanel] = useState(false);
  
  // AI Agent Settings for default mode
  const { settings: aiSettings } = useAIAgentSettings();
  
  // AI Mode management — feed latest conversation IDs from customer groups
  const conversationIds = useMemo(() =>
    customerGroups.map(cg => cg.latest_conversation_id).filter(Boolean),
    [customerGroups]
  );
  const {
    modes: conversationAIModes,
    loading: aiModesLoading,
    updateConversationMode,
    setAllToMode,
  } = useConversationAIModes(conversationIds);
  
  // N-31/N-08: AppLayout's shared tenant frame now shows this alarm for a direct tenant
  // user. A platform admin impersonating a tenant renders through
  // `PlatformAdminNavigation` instead (see AppLayout.tsx's `usePlatformAdminLayout`
  // branch), which the shared frame's banner never reaches -- so this page keeps its own
  // copy for that path, gated below to `isPlatformAdmin` so a direct tenant user does
  // not see the alarm twice. `showToasts` stays off: the toast read platformAdmin.json
  // strings ("Check the WHATSAPP_APP_SECRET setting") straight at whoever was looking,
  // and the persistent alert already says the same thing in business language.
  const { status: webhookStatus, forceCheck: forceWebhookCheck, loading: webhookLoading } = useWebhookErrorDetection({
    tenantId: currentTenant?.id,
    pollInterval: 60000,
    showToasts: false,
  });

  // Deep-link: auto-select customer from ?id=conversationId URL param
  useEffect(() => {
    const targetId = deepLinkIdRef.current;
    if (!targetId || !customerGroups.length || selectedCustomer) return;

    // First try to find customer who owns this conversation ID
    const ownerCustomer = customerGroups.find(cg =>
      cg.all_conversation_ids.includes(targetId) || cg.latest_conversation_id === targetId
    );
    if (ownerCustomer) {
      setSelectedCustomer(ownerCustomer);
      setActiveConversationId(ownerCustomer.latest_conversation_id);
      deepLinkIdRef.current = null;
    } else if (customerGroups.length > 0) {
      // Conversation not in current page — resolve via RPC
      getCustomerIdForConversation(targetId).then(({ data: customerId }) => {
        if (customerId) {
          const customer = customerGroups.find(cg => cg.customer_id === customerId);
          if (customer) {
            setSelectedCustomer(customer);
            setActiveConversationId(customer.latest_conversation_id);
          }
        }
        deepLinkIdRef.current = null;
      });
    }
  }, [customerGroups, selectedCustomer]);

  // Update value refs when values change (doesn't trigger subscription recreation)
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // Cache user ID when available, never clear during session
  useEffect(() => {
    if (userProfile?.id) {
      currentUserIdRef.current = userProfile.id;
    }
  }, [userProfile?.id]);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  // Fetch customer groups and tenant data - ProtectedRoute ensures auth is valid
  useEffect(() => {
    if (user && session && effectiveTenantId) {
      const loadData = async () => {
        await Promise.all([fetchCustomerGroups(), loadTenantData()]);
        setLoading(false);
      };

      loadData();
      const unsubscribe = setupRealtimeSubscription();
      return unsubscribe;
    }
  }, [user?.id, effectiveTenantId]);  // Stable primitive dependencies - prevents reload on token refresh

  // Refetch customer groups when filters change (server-side filtering)
  useEffect(() => {
    if (user && session && effectiveTenantId && !loading) {
      fetchCustomerGroups();
    }
  }, [assignedAgentFilter, filterStatus, aiModeFilter, inboxView]);

  // REMOVED: No longer needed since RealtimeChat drives all updates
  // Background refresh is now only via setupRealtimeSubscription (every 3 minutes)

  const loadTenantData = async () => {
    try {
      console.log('Loading tenant data for tenant ID:', effectiveTenantId);
      setTenantLoading(true);
      
      if (!effectiveTenantId) {
        console.warn('No tenant ID available');
        toast({
          title: t('notifications:error'),
          description: t('conversations:setupRequired'),
          variant: "destructive",
        });
        return;
      }

      // Get tenant details using the effective tenant ID
      const { data, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', effectiveTenantId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        console.log('Tenant loaded successfully:', data.id);
        setCurrentTenant(data);
      } else {
        console.warn('Tenant not found in database');
        toast({
          title: t('notifications:error'),
          description: t('conversations:tenantNotFound'),
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading tenant data:', error);
      toast({
        title: t('notifications:error'),
        description: t('conversations:errorLoadingTenant'),
        variant: "destructive",
      });
    } finally {
      setTenantLoading(false);
    }
  };

  const fetchCustomerGroups = async (loadMore: boolean = false) => {
    if (loadMore && !hasMoreConversations) return;
    if (loadMore) setIsLoadingMore(true);

    try {
      if (import.meta.env.DEV) console.log('[CustomerGroups] Fetching, isInitialLoad:', isInitialLoad.current, 'loadMore:', loadMore);

      // Store current selection to preserve it
      const currentSelectedCustomerId = selectedCustomer?.customer_id;

      const offset = loadMore ? conversationPage * 50 : 0;

      // Server-side filtering via RPC params — no client-side filter/dedup needed
      const { data, totalCount, error: fetchError } = await getTenantCustomersWithConversations({
        limit: 50,
        offset,
        filterAssigned: assignedAgentFilter,
        search: searchTerm || undefined,
        inboxView,
        statusFilter: filterStatus,
        aiModeFilter
      });

      if (totalCount !== undefined) {
        setTotalConversationCount(totalCount);
      }

      if ((data || []).length < 50) {
        setHasMoreConversations(false);
      }

      if (fetchError) throw fetchError;

      const customers: CustomerWithConversations[] = (data || []).map((row: Record<string, unknown>) => ({
        customer_id: row.customer_id,
        customer_name: row.customer_name || '',
        customer_phone: row.customer_phone || '',
        customer_whatsapp_id: row.customer_whatsapp_id || '',
        conversation_count: Number(row.conversation_count) || 1,
        all_conversation_ids: row.all_conversation_ids || [],
        has_pending_escalation: row.has_pending_escalation || false,
        total_unread_count: Number(row.total_unread_count) || 0,
        latest_conversation_id: row.latest_conversation_id,
        status: row.status || 'open',
        priority: Number(row.priority) || 0,
        last_message_at: row.last_message_at,
        created_at: row.created_at,
        agent_mode: row.agent_mode || 'manual',
        inbox_category: row.inbox_category || 'agent_inbox',
        escalation_status: row.escalation_status || 'none',
        platform_metadata: row.platform_metadata,
        assigned_user_id: row.assigned_user_id || '',
        assigned_user_name: row.assigned_user_name || '',
        assigned_user_email: row.assigned_user_email || '',
        thread_id: row.thread_id || '',
        thread_expires_at: row.thread_expires_at || '',
        is_test_conversation: row.is_test_conversation || false,
        last_message_content: row.last_message_content || '',
        last_message_sender_type: row.last_message_sender_type || '',
        total_message_count: Number(row.total_message_count) || 0,
        customer_message_count: Number(row.customer_message_count) || 0,
        ai_auto_message_count: Number(row.ai_auto_message_count) || 0,
        ai_assist_message_count: Number(row.ai_assist_message_count) || 0,
        manual_message_count: Number(row.manual_message_count) || 0,
        window_expires_at: row.window_expires_at || '',
        window_time_remaining_hours: Number(row.window_time_remaining_hours) || 0,
        is_window_active: row.is_window_active || false,
        partner_name: row.partner_name || '',
      }));

      // Pagination logic - append or replace
      if (loadMore) {
        setCustomerGroups(prev => {
          const existingIds = new Set(prev.map(c => c.customer_id));
          const newCustomers = customers.filter(c => !existingIds.has(c.customer_id));
          return [...prev, ...newCustomers];
        });
        setConversationPage(prev => prev + 1);
        setIsLoadingMore(false);
      } else {
        setCustomerGroups(customers);
        setConversationPage(1);
      }

      // Update attention states
      updateConversationAttentionStates(customers);

      // Preserve selected customer during refresh
      if (currentSelectedCustomerId) {
        const stillExists = customers.find(c => c.customer_id === currentSelectedCustomerId);
        if (stillExists) {
          setSelectedCustomer(stillExists);
          setActiveConversationId(stillExists.latest_conversation_id);
        } else if (!loadMore) {
          // Customer no longer in results (filtered out) — clear selection
          if (customers.length > 0 && isInitialLoad.current) {
            setSelectedCustomer(customers[0]);
            setActiveConversationId(customers[0].latest_conversation_id);
          }
        }
      } else if (isInitialLoad.current && customers.length > 0) {
        setSelectedCustomer(customers[0]);
        setActiveConversationId(customers[0].latest_conversation_id);
      }

      isInitialLoad.current = false;
    } catch (err) {
      console.error('Error fetching customer groups:', err);
      error('Failed to fetch customer groups', { error: err });
      toast({
        title: t('notifications:error'),
        description: t('conversations:errorLoadingConversations'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Scroll handler for infinite scroll (70% pre-fetch)
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercent = (scrollTop + clientHeight) / scrollHeight;

    if (scrollPercent >= 0.7 && !isLoadingMore && hasMoreConversations) {
      fetchCustomerGroups(true);
    }
  };

  // REMOVED: updateConversationInList function is no longer needed
  // RealtimeChat now provides rich callback data via handleMessageUpdate
  // which updates conversations in-memory without database calls

  const setupRealtimeSubscription = () => {
    console.log('[Conversations] 🏗️ Setting up conversation list real-time subscription');
    
    
    let channel: unknown = null;
    let refreshInterval: NodeJS.Timeout | null = null;
    
    const setupSubscription = async () => {
      try {
        // Get tenant schema for real-time subscription
        const { data: tenantSchema } = await supabase.rpc('get_user_tenant_schema');
        
        if (!tenantSchema) {
          console.warn('[Conversations] No tenant schema found, falling back to periodic refresh');
          refreshInterval = setInterval(() => {
            console.log('[Conversations] 🔄 Fallback periodic refresh');
            fetchCustomerGroups();
          }, 30000); // 30 seconds fallback
          return;
        }

        console.log('[Conversations] 📡 Setting up real-time subscription for schema:', tenantSchema);
        
        // Create channel for tenant conversations
        channel = supabase
          .channel(`conversations-${tenantSchema}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: tenantSchema,
              table: 'conversations'
            },
            (payload) => {
              if (import.meta.env.DEV) console.log('[CustomerGroups] 🆕 New conversation detected:', payload);
              // Refresh customer groups to show updated conversation count
              fetchCustomerGroups();
            }
          )
          .on(
            'postgres_changes', 
            {
              event: 'UPDATE',
              schema: tenantSchema,
              table: 'conversations'
            },
            async (payload) => {
              console.log('[Conversations] 📝 Conversation updated:', payload);

              // Check if this is an assignment to the current user
              const oldAssignment = payload.old?.assigned_user_id;
              const newAssignment = payload.new?.assigned_user_id;
              const customerName = payload.new?.customer_name || tRef.current('conversations:unknownCustomer', 'Unknown Customer');

              // Show toast if conversation was newly assigned to current user
              if (newAssignment !== oldAssignment && newAssignment === userProfileRef.current?.id) {
                toastRef.current({
                  title: tRef.current('conversations:assignment.newAssignment', 'New Assignment'),
                  description: tRef.current(
                    'conversations:assignment.assignedToConversation',
                    'You have been assigned to {{customerName}}',
                    { customerName }
                  ),
                  duration: 5000,
                });
              }

              // V1409: Merge conversation UPDATE into customer groups
              try {
                const conversationId = payload.new?.id;
                if (!conversationId) return;

                const { data: freshConv, error: queryError } = await supabase
                  .from(`${tenantSchema}.conversations`)
                  .select('id, status, escalation_status, agent_mode, platform_metadata, updated_at, assigned_user_id, customer_name, inbox_category')
                  .eq('id', conversationId)
                  .single();

                if (queryError || !freshConv) {
                  if (queryError?.code === 'PGRST301' || queryError?.code === 'PGRST116') {
                    fetchCustomerGroups();
                  }
                  return;
                }

                // Find which customer group owns this conversation
                setCustomerGroups(prev => prev.map(cg => {
                  if (!cg.all_conversation_ids.includes(freshConv.id)) return cg;

                  // Only update latest conversation fields if this IS the latest
                  if (cg.latest_conversation_id === freshConv.id) {
                    return {
                      ...cg,
                      status: freshConv.status,
                      escalation_status: freshConv.escalation_status,
                      agent_mode: freshConv.agent_mode,
                      platform_metadata: freshConv.platform_metadata,
                      assigned_user_id: freshConv.assigned_user_id,
                      customer_name: freshConv.customer_name || cg.customer_name,
                      inbox_category: freshConv.inbox_category,
                      has_pending_escalation: freshConv.escalation_status === 'pending' || cg.has_pending_escalation
                    };
                  }

                  // Non-latest conversation — only update aggregates
                  return {
                    ...cg,
                    has_pending_escalation: freshConv.escalation_status === 'pending' || cg.has_pending_escalation
                  };
                }));

                // Update selected customer if affected
                setSelectedCustomer(prev => {
                  if (!prev || !prev.all_conversation_ids.includes(freshConv.id)) return prev;
                  if (prev.latest_conversation_id === freshConv.id) {
                    return {
                      ...prev,
                      status: freshConv.status,
                      escalation_status: freshConv.escalation_status,
                      agent_mode: freshConv.agent_mode,
                      platform_metadata: freshConv.platform_metadata,
                      assigned_user_id: freshConv.assigned_user_id,
                      inbox_category: freshConv.inbox_category
                    };
                  }
                  return prev;
                });

              } catch (error) {
                if (error instanceof Error && (error.message.includes('network') || error.message.includes('fetch'))) {
                  fetchCustomerGroups();
                }
              }
            }
          )
          .subscribe((status) => {
            console.log('[Conversations] 📡 Real-time subscription status:', status);
          });

        // Real-time subscription handles updates automatically
        
      } catch (error) {
        console.error('[Conversations] Failed to setup real-time subscription:', error);
        // Fast fallback polling for immediate recovery
        refreshInterval = setInterval(() => {
          console.log('[Conversations] 🔄 Fast error fallback refresh');
          fetchCustomerGroups();
        }, 10000); // 10 seconds on error for quick recovery
      }
    };

    setupSubscription();

    return () => {
      console.log('[Conversations] 🧹 Cleaning up conversation subscriptions');
      if (channel) {
        supabase.removeChannel(channel);
      }
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  };

  // LEGACY: Simplified for backward compatibility - RealtimeChat now drives updates
  const handleMessageSent = (message: AIMessage) => {
    console.log('[Conversations] 📤 Legacy message sent callback - RealtimeChat drives updates now');
    // The enhanced handleMessageUpdate should have already been called by RealtimeChat
    // This is just for backward compatibility and fallback logging
  };

  // Update attention states — uses agent_mode from RPC data directly
  const updateConversationAttentionStates = (customers: CustomerWithConversations[]) => {
    const needingAttention = new Set<string>();

    customers.forEach(customer => {
      const hasUnread = customer.total_unread_count > 0;
      const isIncoming = customer.last_message_sender_type === 'customer';
      const mode = customer.agent_mode || 'manual';

      if (hasUnread && isIncoming && (mode === 'manual' || mode === 'suggestions')) {
        // Add ALL conversation IDs for this customer to attention set
        customer.all_conversation_ids.forEach(id => needingAttention.add(id));
      }
    });

    setConversationsNeedingAttention(needingAttention);
  };

  // Handle AI mode changes from RealtimeChat — update customer group state directly
  const handleAIModeChange = (conversationId: string, newMode: 'auto' | 'suggestions' | 'manual') => {
    if (import.meta.env.DEV) console.log('[CustomerGroups] 🤖 AI mode change:', { conversationId, newMode });
    setCustomerGroups(prev => prev.map(cg =>
      cg.latest_conversation_id === conversationId
        ? { ...cg, agent_mode: newMode }
        : cg
    ));
    setSelectedCustomer(prev =>
      prev?.latest_conversation_id === conversationId
        ? { ...prev, agent_mode: newMode }
        : prev
    );
  };

  // Handle marking conversation as resolved
  // NOTE: RealtimeChat already calculates the NEW category to set, so we use it directly
  const handleMarkResolved = async (conversationId: string, newCategory: string) => {
    if (!currentTenant?.schema_name) {
      toast({ title: t('common:error'), description: t('conversations:noTenantSelected'), variant: 'destructive' });
      return;
    }

    const { error } = await supabase.rpc('update_conversation_inbox_category', {
      tenant_schema: currentTenant.schema_name,
      conversation_id: conversationId,
      category: newCategory
    });

    if (error) {
      console.error('❌ Error updating inbox category:', error);
      toast({
        title: t('common:error'),
        description: `${t('conversations:inbox.updateError')}: ${error.message}`,
        variant: 'destructive'
      });
    } else {
      toast({
        title: t('common:success'),
        description: newCategory === 'resolved'
          ? t('conversations:inbox.markedResolved', 'Conversation marked as resolved')
          : t('conversations:inbox.reopened', 'Conversation reopened')
      });
      fetchCustomerGroups(); // Refresh list
    }
  };

  // Handle message updates from RealtimeChat — update customer group in-place
  const handleMessageUpdate = (update: MessageUpdate) => {
    setCustomerGroups(prev => {
      const updated = prev.map(cg => {
        if (!cg.all_conversation_ids.includes(update.conversationId)) return cg;

        const currentMode = cg.agent_mode || 'manual';
        const statsUpdate = update.type === 'received'
          ? {
              total_message_count: (cg.total_message_count || 0) + 1,
              customer_message_count: (cg.customer_message_count || 0) + 1
            }
          : {
              total_message_count: (cg.total_message_count || 0) + 1,
              ai_auto_message_count: currentMode === 'auto' ? (cg.ai_auto_message_count || 0) + 1 : cg.ai_auto_message_count,
              ai_assist_message_count: currentMode === 'suggestions' ? (cg.ai_assist_message_count || 0) + 1 : cg.ai_assist_message_count,
              manual_message_count: currentMode === 'manual' ? (cg.manual_message_count || 0) + 1 : cg.manual_message_count
            };

        const updatedCustomer: CustomerWithConversations = {
          ...cg,
          last_message_at: update.lastMessageAt || new Date().toISOString(),
          last_message_content: update.lastMessageContent || cg.last_message_content,
          last_message_sender_type: update.type === 'received' ? 'customer' : 'agent',
          total_unread_count: update.type === 'received'
            ? (update.unreadCount !== undefined ? update.unreadCount : cg.total_unread_count + 1)
            : 0,
          customer_name: update.customerName || cg.customer_name,
          customer_phone: update.customerPhone || cg.customer_phone,
          ...statsUpdate
        };

        // Optimistic de-escalation
        if (update.type === 'sent' && cg.escalation_status === 'pending') {
          updatedCustomer.escalation_status = 'resolved';
        }

        return updatedCustomer;
      });

      // FREEZE LIST when a customer is selected (like Slack/Discord)
      if (selectedCustomer !== null) return updated;

      // No customer selected — sort by most recent
      return updated.sort((a, b) => {
        const aTime = new Date(a.last_message_at || a.created_at).getTime();
        const bTime = new Date(b.last_message_at || b.created_at).getTime();
        return bTime - aTime;
      });
    });
  };

  // Customer select — no debounce, ID-only capture to avoid stale closures
  const handleCustomerSelect = useCallback((customerId: string) => {
    setCustomerGroups(prev => {
      const fresh = prev.find(c => c.customer_id === customerId);
      if (fresh) {
        setSelectedCustomer(fresh);
        setActiveConversationId(fresh.latest_conversation_id);
        // Clear attention for ALL conversation IDs of this customer
        setConversationsNeedingAttention(prevAttention => {
          const updated = new Set(prevAttention);
          fresh.all_conversation_ids.forEach(id => updated.delete(id));
          return updated;
        });
      }
      return prev; // no mutation
    });
  }, []);

  // Derive fresh status from live window data (calculated at query time by RPC)
  const deriveConversationStatus = (customer: CustomerWithConversations): string => {
    if (customer.status === 'closed') return 'closed';
    if (customer.is_window_active && customer.window_time_remaining_hours != null) {
      if (customer.window_time_remaining_hours > 3) return 'open';
      if (customer.window_time_remaining_hours > 0) return 'expiring_soon';
      return 'template_only';
    }
    if (customer.is_window_active === false) return 'template_only';
    return customer.status;
  };

  // Resolve "me" filter to actual user ID for comparison
  const effectiveAssignedFilter = assignedAgentFilter === 'me' ? userProfile?.id : assignedAgentFilter;

  const getStatusConfig = (status: string, hoursRemaining?: number) => {
    switch (status) {
      case 'open':
        return {
          color: 'bg-green-100 text-green-800',
          icon: '🟢',
          text: t('conversations:status.open')
        };
      case 'expiring_soon':
        return {
          color: 'bg-orange-100 text-orange-800 animate-pulse',
          icon: '⚠️',
          text: hoursRemaining ? t('conversations:status.expiringIn', { hours: Math.floor(hoursRemaining) }) : t('conversations:status.expiringSoon')
        };
      case 'template_only':
        return {
          color: 'bg-yellow-100 text-yellow-800',
          icon: '📋',
          text: t('conversations:status.templateOnly')
        };
      case 'closed':
        return {
          color: 'bg-gray-100 text-gray-800',
          icon: '🔒',
          text: t('conversations:status.closed')
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          icon: '',
          text: status
        };
    }
  };

  // Server-side filtering — client only sorts for escalation priority
  const filteredCustomers = useMemo(() => {
    return [...customerGroups].sort((a, b) => {
      // Escalated customers always appear first
      const aEscalated = a.has_pending_escalation ? 1 : 0;
      const bEscalated = b.has_pending_escalation ? 1 : 0;
      if (aEscalated !== bEscalated) return bEscalated - aEscalated;

      // Then sort by last message time
      const aTime = new Date(a.last_message_at || a.created_at).getTime();
      const bTime = new Date(b.last_message_at || b.created_at).getTime();
      return assignedAgentFilter === 'unassigned'
        ? aTime - bTime  // FIFO for unassigned
        : bTime - aTime; // Newest first
    });
  }, [customerGroups, assignedAgentFilter]);

  // N-16: an absolute timestamp in the browser's own locale ("4/6/2026, 11:23:36 PM")
  // reads as ambiguous between day and month for anyone not on that locale. The
  // visible label is now relative ("hace 3 meses"); the exact local-format timestamp
  // moves to a tooltip so it is still one hover away, never lost.
  const dateFnsLocale = i18n.language?.toLowerCase().startsWith('es') ? dateFnsEs : dateFnsEnUS;

  const formatTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: dateFnsLocale });
  };

  const formatTimeTooltip = (dateString: string) => {
    return new Intl.DateTimeFormat(i18n.language || undefined, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date(dateString));
  };

  // N-16: knowing whether the assistant replied or a named teammate did is what an
  // operator needs before typing. The RPC's last_message_sender_type does distinguish the
  // assistant from a human reply (ai_agent_automatic / ai_agent_suggestion vs agent /
  // user), so that split is real; it does not carry the human's name, which would
  // need `messages.sender_uuid` joined in on the backend -- flagged separately, not
  // guessed here.
  const formatLastMessageInfo = (customer: CustomerWithConversations) => {
    if (!customer.last_message_content) return t('conversations:noMessages');

    const senderType = customer.last_message_sender_type;
    const senderName = senderType === 'customer'
      ? (customer.customer_name || t('conversations:customer'))
      : senderType === 'system'
      ? t('conversations:lastMessageSenderSystem')
      : senderType === 'ai_agent_automatic' || senderType === 'ai_agent_suggestion'
      ? t('conversations:lastMessageSenderAssistant')
      : t('conversations:chat.sender.agent');

    const timestamp = new Date(customer.last_message_at).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });

    return t('conversations:lastMessageBy', { sender: senderName, time: timestamp });
  };

  // Keep ref in sync so debounced callback always calls latest version
  fetchCustomerGroupsRef.current = fetchCustomerGroups;

  // Search debounce handler — 300ms debounce for server-side search
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchCustomerGroupsRef.current();
    }, 300);
  }, []);

  // Draft management handlers — keyed by customerId
  const handleSaveDraft = useCallback((customerId: string, draft: string) => {
    setMessageDrafts(prev => {
      const newDrafts = new Map(prev);

      if (draft.trim()) {
        newDrafts.set(customerId, draft);
      } else {
        newDrafts.delete(customerId);
      }

      // Optional: Persist to localStorage for browser refresh recovery
      try {
        const entries = Array.from(newDrafts.entries());
        localStorage.setItem('okos_conversation_drafts', JSON.stringify(entries));
        console.log('[V1411] 📝 Persisted to localStorage:', newDrafts.size, 'draft(s)');
      } catch (error) {
        console.error('[V1411] ❌ Failed to save draft to localStorage:', error);
      }

      return newDrafts;
    });
  }, []);

  const handleClearDraft = useCallback((customerId: string) => {
    setMessageDrafts(prev => {
      const newDrafts = new Map(prev);
      newDrafts.delete(customerId);

      // Update localStorage
      try {
        const entries = Array.from(newDrafts.entries());
        localStorage.setItem('okos_conversation_drafts', JSON.stringify(entries));
      } catch (error) {
        console.error('[V1411] Failed to update localStorage:', error);
      }

      return newDrafts;
    });
  }, []);

  const getDraft = useCallback((customerId: string): string => {
    return messageDrafts.get(customerId) || '';
  }, [messageDrafts]);

  // Show loading while loading data - ProtectedRoute handles auth validation
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">{t('conversations:loadingConversations')}</p>
        </div>
      </div>
    );
  }

  return (
    <Frame>
      <div className="h-screen bg-background flex flex-col overflow-hidden">
        {/* N-31: AppLayout's shared frame shows this alarm for a direct tenant user.
            Kept here too, gated to platform-admin impersonation -- see the hook
            wiring above for why that path needs its own copy. */}
        {isPlatformAdmin && !webhookLoading && webhookStatus && (webhookStatus.authenticationFailing || !webhookStatus.isHealthy || webhookStatus.recentErrors.length > 0) && (
          <div className="flex-shrink-0 border-b bg-background">
            <div className="container mx-auto px-4 py-1">
              <WebhookErrorAlert
                status={webhookStatus}
                onRetry={forceWebhookCheck}
                isRetrying={webhookLoading}
                className="mb-0"
                canReconnect={userProfile?.role === 'tenant_admin' || userProfile?.role === 'platform_admin'}
                reconnectHref="/whatsapp-admin"
              />
            </div>
          </div>
        )}

      {/* Resizable Panels - Takes remaining space */}
      <div className="flex-1 min-h-0">
        {isMobile ? (
          // N-13: this used to render both columns side by side at 30%/70% of a phone's
          // width, dragged with a resize handle meant for a mouse -- neither slice was wide
          // enough for its own content, which is why the page measured 900px against a
          // 390px viewport instead of wrapping. One full-width step at a time now: the list,
          // or the open conversation, never both -- with a back button to return (below).
          <div className="h-full">
            {!selectedCustomer && (
            <div className="h-full flex flex-col bg-card">
              {/* Bulk AI Mode Controls */}
              <BulkAIModeControls
                onSetAllToMode={setAllToMode}
                onFilterChange={setAiModeFilter}
                currentFilter={aiModeFilter}
                conversationCount={filteredCustomers.length}
                totalCount={totalConversationCount}
                loading={aiModesLoading}
              />

              <div className="border-b p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-5 w-5" />
                  <h3 className="font-semibold">{t('conversations:searchAndFilter')}</h3>
                  <ConversationsVersionToggle className="ml-auto" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t('conversations:search')}
                      aria-label={t('conversations:search')}
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-10 h-8"
                    />
                  </div>
                  <Select value={inboxView} onValueChange={(value: 'active' | 'resolved' | 'all') => setInboxView(value)}>
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue>
                        {inboxView === 'active' && `📥 ${t('conversations:inbox.active', 'Active')}`}
                        {inboxView === 'resolved' && `✅ ${t('conversations:inbox.resolved', 'Resolved')}`}
                        {inboxView === 'all' && `🗂️ ${t('conversations:inbox.all', 'All')}`}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">📥 {t('conversations:inbox.active', 'Active')}</SelectItem>
                      <SelectItem value="resolved">✅ {t('conversations:inbox.resolved', 'Resolved')}</SelectItem>
                      <SelectItem value="all">🗂️ {t('conversations:inbox.all', 'All')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={assignedAgentFilter} onValueChange={setAssignedAgentFilter}>
                    <SelectTrigger className="w-[180px] h-8">
                      <SelectValue placeholder={t('conversations:assignment.filterByAgent', 'Assigned Agent')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('conversations:assignment.allAgents', 'All Agents')}</SelectItem>
                      <SelectItem value="me">{t('conversations:assignment.assignedToMe', 'Assigned to Me')}</SelectItem>
                      <SelectItem value="unassigned">{t('conversations:assignment.unassigned', 'Unassigned')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-y-auto" onScroll={handleScroll}>
                  {filteredCustomers.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t('conversations:noConversationsYet')}</p>
                      <p className="text-sm">{t('conversations:messagesWillAppear')}</p>
                    </div>
                  ) : (
                    filteredCustomers.map((customer) => (
                       <div
                         key={customer.customer_id}
                         className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-all duration-75 ${
                           selectedCustomer?.customer_id === customer.customer_id ? 'shadow-[0_0_20px_8px_hsl(var(--primary)/0.35)] bg-muted' : ''
                         } ${
                           customer.has_pending_escalation
                             ? 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-900/20'
                             : customer.assigned_user_id === userProfile?.id
                             ? 'border-l-4 border-l-blue-500 bg-blue-50/30 dark:bg-blue-900/20'
                             : customer.all_conversation_ids.some(id => conversationsNeedingAttention.has(id))
                             ? 'animate-pulse border-l-4 border-l-primary bg-primary/5'
                             : customer.inbox_category === 'resolved'
                             ? 'border-l-4 border-l-gray-400 bg-gray-50/50 dark:bg-gray-800/20 opacity-60'
                             : ''
                         }`}
                         onClick={() => handleCustomerSelect(customer.customer_id)}
                       >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <span className="font-medium">
                                {customer.customer_name || t('conversations:customerFallback', { id: customer.customer_id.slice(0, 8) })}
                              </span>
                              {customer.customer_phone && (
                                <div className="text-xs text-muted-foreground">
                                  {customer.customer_phone}
                                </div>
                              )}
                            </div>
                          </div>
                           <div className="flex items-center gap-2 flex-wrap">
                             {/* Conversation count badge */}
                             {customer.conversation_count > 1 && (
                               <Badge variant="secondary" className="text-xs">
                                 {t('conversations:conversationCount', { count: customer.conversation_count })}
                               </Badge>
                             )}
                             {/* Escalation Badge */}
                             {customer.has_pending_escalation && customer.platform_metadata?.escalation_context && (
                               <EscalationBadge
                                 trigger={customer.platform_metadata.escalation_context.trigger}
                                 animate={true}
                               />
                             )}
                             {/* AI Mode Indicator — from RPC data directly */}
                             <ConversationAIModeIndicator
                               mode={(customer.agent_mode as 'auto' | 'suggestions' | 'manual') || 'manual'}
                               hasNewMessage={customer.all_conversation_ids.some(id => conversationsNeedingAttention.has(id))}
                               size="sm"
                             />
                             {/* Partner Badge -- a decorative brand marker (this customer belongs to a
                                 named partner), not a lifecycle status, and purple has no equivalent among
                                 the eight Badge variants. Kept hand-painted on purpose. */}
                             {customer.partner_name && (
                               <Badge className="text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 flex items-center gap-1">
                                 <Handshake className="h-3 w-3" />
                                 {t('conversations:partnerBadge', { name: customer.partner_name })}
                               </Badge>
                             )}
                             {(() => {
                               const derivedStatus = deriveConversationStatus(customer);
                               const statusCfg = getStatusConfig(derivedStatus, customer.window_time_remaining_hours);
                               return (
                                 <Badge
                                   className={`text-xs ${statusCfg.color} ${
                                     derivedStatus === 'template_only' ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                                   }`}
                                   onClick={(e) => {
                                     if (derivedStatus === 'template_only') {
                                       e.stopPropagation();
                                       setTemplateSelectorConversation(customer);
                                       setShowTemplateSelectorDialog(true);
                                     }
                                   }}
                                 >
                                   {statusCfg.icon} {statusCfg.text}
                                 </Badge>
                               );
                             })()}
                             {customer.total_unread_count > 0 && (
                               <Badge variant="destructive" className="text-xs">
                                 {customer.total_unread_count}
                               </Badge>
                             )}
                           </div>
                        </div>
                        <div className="text-sm text-muted-foreground mb-2 truncate">
                          {formatLastMessageInfo(customer)}
                        </div>

                        {/* Message Statistics Counter */}
                        <div className="mb-2">
                          <MessageStatsCounter
                            totalCount={customer.total_message_count}
                            customerCount={customer.customer_message_count}
                            aiAutoCount={customer.ai_auto_message_count}
                            aiAssistCount={customer.ai_assist_message_count}
                            manualCount={customer.manual_message_count}
                          />
                        </div>

                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {customer.last_message_at ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>{formatTime(customer.last_message_at)}</span>
                                </TooltipTrigger>
                                <TooltipContent>{formatTimeTooltip(customer.last_message_at)}</TooltipContent>
                              </Tooltip>
                            ) : (
                              t('conversations:noMessages')
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs">{t('conversations:priorityLabel', { level: customer.priority })}</span>
                            {customer.customer_whatsapp_id && (
                              <Badge variant="outline" className="text-xs">
                                WhatsApp
                              </Badge>
                            )}
                            {customer.window_expires_at && (
                              <Badge
                                variant={customer.is_window_active ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {customer.is_window_active
                                  ? `⏱️ ${t('conversations:window.hoursLeft', { hours: Math.floor(customer.window_time_remaining_hours || 0) })}`
                                  : customer.status === 'closed'
                                    ? `🔒 ${t('conversations:status.closed')}`
                                    : `📋 ${t('conversations:window.expiredShort')}`
                                }
                              </Badge>
                            )}
                            {customer.agent_mode === 'manual' && (
                              <AssignmentDropdown
                                conversationId={customer.latest_conversation_id}
                                currentAssignedUserId={customer.assigned_user_id}
                                currentAssignedUserName={customer.assigned_user_name}
                                currentAssignedUserEmail={customer.assigned_user_email}
                              />
                            )}
                            {effectiveAssignedFilter && effectiveAssignedFilter !== 'all' && effectiveAssignedFilter !== 'unassigned' &&
                             customer.assigned_user_id !== effectiveAssignedFilter && (
                              <span className="text-xs text-muted-foreground/60 dark:text-muted-foreground/40 italic">
                                {t('conversations:assignment.assignedToOlderConv', 'Assigned in older conversation')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Chat/Context Panel (Mobile - Toggleable) */}
            {selectedCustomer && (
            <div className="h-full">
            {showMobileContextPanel ? (
              // Show Context Panel
              <div className="h-full flex flex-col">
                <div className="flex-1 min-h-0 overflow-y-auto">
                {selectedCustomer?.escalation_status === 'pending' &&
                selectedCustomer.platform_metadata?.escalation_context &&
                currentTenant ? (
                  <div className="h-full flex flex-col">
                    <div className="flex items-center justify-between border-b p-4 bg-background">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Bot className="h-5 w-5" />
                        {t('conversations:contextPanel.title')}
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMobileContextPanel(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <EscalationContextPanel
                        conversationId={activeConversationId || selectedCustomer.latest_conversation_id}
                        tenantSchema={currentTenant.schema_name}
                        escalationContext={selectedCustomer.platform_metadata.escalation_context}
                        onResolved={async () => {
                          setShowMobileContextPanel(false);
                          if (selectedCustomer?.status === 'template_only' && currentTenant?.schema_name) {
                            await supabase.rpc('update_conversation_inbox_category', {
                              tenant_schema: currentTenant.schema_name,
                              conversation_id: selectedCustomer.latest_conversation_id,
                              category: 'resolved'
                            });
                            setCustomerGroups(prev => prev.map(cg =>
                              cg.customer_id === selectedCustomer.customer_id ? { ...cg, inbox_category: 'resolved' } : cg
                            ));
                            setSelectedCustomer(null);
                            setActiveConversationId(null);
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <Card className="h-full flex flex-col overflow-hidden border-0 rounded-none">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Bot className="h-5 w-5" />
                          {t('conversations:aiPanelTitle')}
                        </CardTitle>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowMobileContextPanel(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <CardDescription>
                        {t('conversations:aiPanelDesc')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex items-center justify-center">
                      <div className="text-center text-muted-foreground">
                        <div className="h-24 w-24 mx-auto mb-4 rounded-lg bg-muted/50 flex items-center justify-center">
                          <Bot className="h-12 w-12 opacity-50" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">{t('common:comingSoon')}</h3>
                        <p className="text-sm">{t('conversations:aiPanelComingSoon')}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
                </div>
                <StuckConversationActions
                  tenantSchema={currentTenant?.schema_name as string | undefined}
                  conversationId={activeConversationId || selectedCustomer?.latest_conversation_id || null}
                />
              </div>
            ) : (
              // Show Chat Panel
              <Card className="h-full flex flex-col overflow-hidden border-0 rounded-none">
                {selectedCustomer ? (
                  <div className="h-full flex flex-col">
                    {/* Mobile Chat Header */}
                    <div className="flex items-center justify-between border-b p-3 bg-background">
                      <div className="flex items-center gap-2 min-w-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-shrink-0 -ml-2"
                          onClick={() => setSelectedCustomer(null)}
                          aria-label={t('conversations:backToList')}
                        >
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <User className="h-4 w-4 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm">
                            {selectedCustomer.customer_name || t('conversations:customerFallback', { id: selectedCustomer.customer_id.slice(0, 8) })}
                          </p>
                          {selectedCustomer.customer_phone && (
                            <p className="text-xs text-muted-foreground">
                              {selectedCustomer.customer_phone}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setShowMobileContextPanel(true)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <RealtimeChat
                      conversationId={activeConversationId}
                      customerId={selectedCustomer.customer_id}
                      customerName={selectedCustomer.customer_name || t('conversations:customerFallback', { id: selectedCustomer.customer_id.slice(0, 8) })}
                      customerPhone={selectedCustomer.customer_phone}
                      overrideTenantId={effectiveTenantId}
                      hideCustomerIdentity
                      inboxCategory={selectedCustomer.inbox_category}
                      onInboxCategoryChange={handleMarkResolved}
                      onMessageSent={handleMessageSent}
                      onMessageUpdate={handleMessageUpdate}
                      onAIModeChange={handleAIModeChange}
                      initialDraft={getDraft(selectedCustomer.customer_id)}
                      onDraftChange={(draft) => handleSaveDraft(selectedCustomer.customer_id, draft)}
                      onDraftClear={() => handleClearDraft(selectedCustomer.customer_id)}
                      allConversationIds={selectedCustomer.all_conversation_ids}
                    />
                  </div>
                ) : (
                  <CardContent className="flex items-center justify-center h-full">
                    <div className="text-center text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">{t('conversations:selectConversationTitle')}</h3>
                      <p>{t('conversations:selectConversationDesc')}</p>
                      <div className="flex items-center justify-center gap-2 mt-4 text-sm">
                        <Bot className="h-4 w-4" />
                        <span>{t('conversations:realtimeChat')}</span>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
            </div>
            )}
          </div>
        ) : (
          // Desktop Layout: 3-column (List + Chat + Context)
          <ResizablePanelGroup direction="horizontal" className="h-full">
            {/* Customer List Panel */}
            <ResizablePanel defaultSize={35} minSize={20} maxSize={40}>
            <div className="h-full flex flex-col bg-card border-r">
              {/* Bulk AI Mode Controls */}
              <BulkAIModeControls
                onSetAllToMode={setAllToMode}
                onFilterChange={setAiModeFilter}
                currentFilter={aiModeFilter}
                conversationCount={filteredCustomers.length}
                totalCount={totalConversationCount}
                loading={aiModesLoading}
              />

              <div className="border-b p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-5 w-5" />
                  <h3 className="font-semibold">{t('conversations:searchAndFilter')}</h3>
                  <ConversationsVersionToggle className="ml-auto" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t('conversations:search')}
                      aria-label={t('conversations:search')}
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-10 h-8"
                    />
                  </div>
                  <Select value={inboxView} onValueChange={(value: 'active' | 'resolved' | 'all') => setInboxView(value)}>
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue>
                        {inboxView === 'active' && `📥 ${t('conversations:inbox.active', 'Active')}`}
                        {inboxView === 'resolved' && `✅ ${t('conversations:inbox.resolved', 'Resolved')}`}
                        {inboxView === 'all' && `🗂️ ${t('conversations:inbox.all', 'All')}`}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">📥 {t('conversations:inbox.active', 'Active')}</SelectItem>
                      <SelectItem value="resolved">✅ {t('conversations:inbox.resolved', 'Resolved')}</SelectItem>
                      <SelectItem value="all">🗂️ {t('conversations:inbox.all', 'All')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={assignedAgentFilter} onValueChange={setAssignedAgentFilter}>
                    <SelectTrigger className="w-[180px] h-8">
                      <SelectValue placeholder={t('conversations:assignment.filterByAgent', 'Assigned Agent')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('conversations:assignment.allAgents', 'All Agents')}</SelectItem>
                      <SelectItem value="me">{t('conversations:assignment.assignedToMe', 'Assigned to Me')}</SelectItem>
                      <SelectItem value="unassigned">{t('conversations:assignment.unassigned', 'Unassigned')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-y-auto" onScroll={handleScroll}>
                  {filteredCustomers.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t('conversations:noConversationsYet')}</p>
                      <p className="text-sm">{t('conversations:messagesWillAppear')}</p>
                    </div>
                  ) : (
                    filteredCustomers.map((customer) => (
                       <div
                         key={customer.customer_id}
                         className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-all duration-75 ${
                           selectedCustomer?.customer_id === customer.customer_id ? 'shadow-[0_0_20px_8px_hsl(var(--primary)/0.35)] bg-muted' : ''
                         } ${
                           customer.has_pending_escalation
                             ? 'border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-900/20'
                             : customer.assigned_user_id === userProfile?.id
                             ? 'border-l-4 border-l-blue-500 bg-blue-50/30 dark:bg-blue-900/20'
                             : customer.all_conversation_ids.some(id => conversationsNeedingAttention.has(id))
                             ? 'animate-pulse border-l-4 border-l-primary bg-primary/5'
                             : customer.inbox_category === 'resolved'
                             ? 'border-l-4 border-l-gray-400 bg-gray-50/50 dark:bg-gray-800/20 opacity-60'
                             : ''
                         }`}
                         onClick={() => handleCustomerSelect(customer.customer_id)}
                       >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <span className="font-medium">
                                {customer.customer_name || t('conversations:customerFallback', { id: customer.customer_id.slice(0, 8) })}
                              </span>
                              {customer.customer_phone && (
                                <div className="text-xs text-muted-foreground">
                                  {customer.customer_phone}
                                </div>
                              )}
                            </div>
                          </div>
                           <div className="flex items-center gap-2 flex-wrap">
                             {/* Conversation count badge */}
                             {customer.conversation_count > 1 && (
                               <Badge variant="secondary" className="text-xs">
                                 {t('conversations:conversationCount', { count: customer.conversation_count })}
                               </Badge>
                             )}
                             {/* Escalation Badge */}
                             {customer.has_pending_escalation && customer.platform_metadata?.escalation_context && (
                               <EscalationBadge
                                 trigger={customer.platform_metadata.escalation_context.trigger}
                                 animate={true}
                               />
                             )}
                             {/* AI Mode Indicator — from RPC data directly */}
                             <ConversationAIModeIndicator
                               mode={(customer.agent_mode as 'auto' | 'suggestions' | 'manual') || 'manual'}
                               hasNewMessage={customer.all_conversation_ids.some(id => conversationsNeedingAttention.has(id))}
                               size="sm"
                             />
                             {/* Partner Badge -- a decorative brand marker (this customer belongs to a
                                 named partner), not a lifecycle status, and purple has no equivalent among
                                 the eight Badge variants. Kept hand-painted on purpose. */}
                             {customer.partner_name && (
                               <Badge className="text-xs bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700 flex items-center gap-1">
                                 <Handshake className="h-3 w-3" />
                                 {t('conversations:partnerBadge', { name: customer.partner_name })}
                               </Badge>
                             )}
                             {(() => {
                               const derivedStatus = deriveConversationStatus(customer);
                               const statusCfg = getStatusConfig(derivedStatus, customer.window_time_remaining_hours);
                               return (
                                 <Badge
                                   className={`text-xs ${statusCfg.color} ${
                                     derivedStatus === 'template_only' ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                                   }`}
                                   onClick={(e) => {
                                     if (derivedStatus === 'template_only') {
                                       e.stopPropagation();
                                       setTemplateSelectorConversation(customer);
                                       setShowTemplateSelectorDialog(true);
                                     }
                                   }}
                                 >
                                   {statusCfg.icon} {statusCfg.text}
                                 </Badge>
                               );
                             })()}
                             {customer.total_unread_count > 0 && (
                               <Badge variant="destructive" className="text-xs">
                                 {customer.total_unread_count}
                               </Badge>
                             )}
                           </div>
                        </div>
                        <div className="text-sm text-muted-foreground mb-2 truncate">
                          {formatLastMessageInfo(customer)}
                        </div>

                        {/* Message Statistics Counter */}
                        <div className="mb-2">
                          <MessageStatsCounter
                            totalCount={customer.total_message_count}
                            customerCount={customer.customer_message_count}
                            aiAutoCount={customer.ai_auto_message_count}
                            aiAssistCount={customer.ai_assist_message_count}
                            manualCount={customer.manual_message_count}
                          />
                        </div>

                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {customer.last_message_at ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>{formatTime(customer.last_message_at)}</span>
                                </TooltipTrigger>
                                <TooltipContent>{formatTimeTooltip(customer.last_message_at)}</TooltipContent>
                              </Tooltip>
                            ) : (
                              t('conversations:noMessages')
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs">{t('conversations:priorityLabel', { level: customer.priority })}</span>
                            {customer.customer_whatsapp_id && (
                              <Badge variant="outline" className="text-xs">
                                WhatsApp
                              </Badge>
                            )}
                            {customer.window_expires_at && (
                              <Badge
                                variant={customer.is_window_active ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {customer.is_window_active
                                  ? `⏱️ ${t('conversations:window.hoursLeft', { hours: Math.floor(customer.window_time_remaining_hours || 0) })}`
                                  : customer.status === 'closed'
                                    ? `🔒 ${t('conversations:status.closed')}`
                                    : `📋 ${t('conversations:window.expiredShort')}`
                                }
                              </Badge>
                            )}
                            {customer.agent_mode === 'manual' && (
                              <AssignmentDropdown
                                conversationId={customer.latest_conversation_id}
                                currentAssignedUserId={customer.assigned_user_id}
                                currentAssignedUserName={customer.assigned_user_name}
                                currentAssignedUserEmail={customer.assigned_user_email}
                              />
                            )}
                            {effectiveAssignedFilter && effectiveAssignedFilter !== 'all' && effectiveAssignedFilter !== 'unassigned' &&
                             customer.assigned_user_id !== effectiveAssignedFilter && (
                              <span className="text-xs text-muted-foreground/60 dark:text-muted-foreground/40 italic">
                                {t('conversations:assignment.assignedToOlderConv', 'Assigned in older conversation')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Chat Interface Panel */}
          <ResizablePanel defaultSize={30} minSize={25} maxSize={35}>
            <Card className="h-full flex flex-col overflow-hidden border-0 rounded-none">
              {selectedCustomer ? (
                <div className="h-full">
                  <RealtimeChat
                    conversationId={activeConversationId}
                    customerId={selectedCustomer.customer_id}
                    customerName={selectedCustomer.customer_name || t('conversations:customerFallback', { id: selectedCustomer.customer_id.slice(0, 8) })}
                    customerPhone={selectedCustomer.customer_phone}
                    overrideTenantId={effectiveTenantId}
                    inboxCategory={selectedCustomer.inbox_category}
                    onInboxCategoryChange={handleMarkResolved}
                    onMessageSent={handleMessageSent}
                    onMessageUpdate={handleMessageUpdate}
                    onAIModeChange={handleAIModeChange}
                    initialDraft={getDraft(selectedCustomer.customer_id)}
                    onDraftChange={(draft) => handleSaveDraft(selectedCustomer.customer_id, draft)}
                    onDraftClear={() => handleClearDraft(selectedCustomer.customer_id)}
                    allConversationIds={selectedCustomer.all_conversation_ids}
                  />
                </div>
              ) : (
                <CardContent className="flex items-center justify-center h-full">
                  <div className="text-center text-muted-foreground">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <h3 className="text-lg font-medium mb-2">{t('conversations:selectConversationTitle')}</h3>
                    <p>{t('conversations:selectConversationDesc')}</p>
                    <div className="flex items-center justify-center gap-2 mt-4 text-sm">
                      <Bot className="h-4 w-4" />
                      <span>{t('conversations:realtimeChat')}</span>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Right Panel - Escalation Context or AI Panel */}
          <ResizablePanel
            defaultSize={35}
            minSize={25}
          >
            <div className="h-full flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto">
              {selectedCustomer?.escalation_status === 'pending' &&
               selectedCustomer.platform_metadata?.escalation_context &&
               currentTenant ? (
                <div className="h-full overflow-y-auto">
                <EscalationContextPanel
                  conversationId={activeConversationId || selectedCustomer.latest_conversation_id}
                  tenantSchema={currentTenant.schema_name}
                  escalationContext={selectedCustomer.platform_metadata.escalation_context}
                  onResolved={async () => {
                    if (selectedCustomer?.status === 'template_only' && currentTenant?.schema_name) {
                      await supabase.rpc('update_conversation_inbox_category', {
                        tenant_schema: currentTenant.schema_name,
                        conversation_id: selectedCustomer.latest_conversation_id,
                        category: 'resolved'
                      });
                      setCustomerGroups(prev => prev.map(cg =>
                        cg.customer_id === selectedCustomer.customer_id ? { ...cg, inbox_category: 'resolved' } : cg
                      ));
                      setSelectedCustomer(null);
                      setActiveConversationId(null);
                    }
                  }}
                />
              </div>
            ) : (
              <Card className="h-full flex flex-col overflow-hidden border-0 rounded-none">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Bot className="h-5 w-5" />
                    {t('conversations:aiPanelTitle')}
                  </CardTitle>
                  <CardDescription>
                    {t('conversations:aiPanelDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <div className="h-24 w-24 mx-auto mb-4 rounded-lg bg-muted/50 flex items-center justify-center">
                      <Bot className="h-12 w-12 opacity-50" />
                    </div>
                    <h3 className="text-lg font-medium mb-2">{t('common:comingSoon')}</h3>
                    <p className="text-sm">{t('conversations:aiPanelComingSoon')}</p>
                  </div>
                </CardContent>
              </Card>
            )}
              </div>
              <StuckConversationActions
                tenantSchema={currentTenant?.schema_name as string | undefined}
                conversationId={activeConversationId || selectedCustomer?.latest_conversation_id || null}
              />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
        )}
      </div>
      </div>

      {/* Template Selector Dialog */}
      <Dialog open={showTemplateSelectorDialog} onOpenChange={setShowTemplateSelectorDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('template.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('template.dialogDesc', { customer: templateSelectorConversation?.customer?.name || t('conversations:customer') })}
            </DialogDescription>
          </DialogHeader>
          <WhatsAppTemplateSelector
            conversationId={templateSelectorConversation?.latest_conversation_id}
            customerPhone={templateSelectorConversation?.customer_phone}
            customerName={templateSelectorConversation?.customer_name}
            onTemplateSent={() => {
              setShowTemplateSelectorDialog(false);
              toast({
                title: t('notifications:success'),
                description: t('conversations:templateSent'),
              });
              // Real-time subscription will update the conversation list
            }}
            onCancel={() => setShowTemplateSelectorDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </Frame>
  );
};

export default Conversations;
